"use client";

import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import Link from "next/link";
import { Bookmark, Pencil, Search, Bell } from "lucide-react";
import { CommerceIcons } from "@/lib/icons/commerce-icons";
import { cn, formatPrice } from "@/lib/utils";
import { ListingImage } from "@/components/ui/listing-image";
import { TILE } from "@/lib/images/presets";
import { resolveStorageUrl } from "@/lib/storage/public-url";
import { buildItemCardView, type ItemCardCtaIcon } from "@/lib/build-item-card-view";
import { itemCard } from "@/lib/item-card-tokens";
import { formatAuctionCountdown } from "@/lib/format-auction-countdown";
import { UnifiedAddToCartButton } from "@/components/UnifiedAddToCartButton";
import { BookmarkLoginModal, type BookmarkModalListing } from "@/components/search/product/bookmark-login-modal";
import { editListingEvents } from "@/lib/edit-listing-events";
import { useSavedState } from "@/hooks/use-saved-state";
import { createClient } from "@/utils/supabase/client";

export interface ItemCardProps {
  listingId: string;
  title: string;
  price: string;
  currency?: string | null;
  image_urls: string | string[];
  scheduledPublishAt?: string | null;
  listingFormat?: string | null;
  discoverTypeLabel?: string | null;
  stock?: number;
  sellerBanned?: boolean;
  isUnlisted?: boolean;
  onClick?: () => void;
  interactive?: boolean;
  imageLoading?: "lazy" | "eager";
  className?: string;
  currentUserId?: string;
  sellerId?: string;
  sellerName?: string;
  sellerAvatarUrl?: string | null;
  condition?: string;
  description?: string;
  grade?: string;
  cardDetails?: {
    tcg_name?: string | null;
    set_name?: string | null;
    rarity_name?: string | null;
    condition_name?: string | null;
    grading_company_name?: string | null;
    grade_value?: number | null;
    grade_label?: string | null;
    badges?: string[];
    defects?: string[];
  } | null;
  shippingMethods?: unknown[];
  cartAction?: "unified" | "static";
  buyNow?: boolean;
  listingType?: string | null;
  // Phase 3 (2026-06-29): `acceptsOffers` prop dropped — derive from
  // `listingFormat` via `isOfferEnabledFormat()`.
  offersPendingCount?: number | null;
  auctionEndAt?: string | null;
  auctionStatus?: string | null;
  auctionBidCount?: number | null;
  initialSaved?: boolean;
  savesCount?: number;
  showBookmark?: boolean;
  showCta?: boolean;
}

function ctaIcon(icon: ItemCardCtaIcon | null) {
  if (icon === "gavel") return <CommerceIcons.bid className="size-3.5 shrink-0" />;
  if (icon === "offer") return <CommerceIcons.offer className="size-3.5 shrink-0" />;
  if (icon === "bell") return <Bell className="size-3.5 shrink-0" />;
  if (icon === "search") return <Search className="size-3.5 shrink-0" />;
  if (icon === "pencil") return <Pencil className="size-3.5 shrink-0" />;
  if (icon === "bag") return <CommerceIcons.buyNow className="size-3.5 shrink-0" />;
  return null;
}

function PulsingDot() {
  return (
    <span
      className="inline-block size-[7px] shrink-0 animate-pulse rounded-full"
      style={{ backgroundColor: itemCard.pulsingDot }}
    />
  );
}

function StatusPill({
  text,
  mode,
  accentColor,
}: {
  text: string;
  mode: "urgent" | "soft";
  accentColor?: string;
}) {
  if (mode === "urgent") {
    return (
      <span
        className="absolute left-2.5 top-2.5 z-10 inline-flex max-w-[calc(100%-3.5rem)] items-center gap-1.5 rounded-full py-1 pl-2 pr-2.5 text-[11px] font-bold tracking-wide"
        style={{ backgroundColor: itemCard.urgentPill.bg, color: itemCard.urgentPill.fg }}
      >
        <PulsingDot />
        <span className="min-w-0 truncate">{text}</span>
      </span>
    );
  }
  return (
    <span
      className="absolute left-2.5 top-2.5 z-10 inline-flex max-w-[calc(100%-3.5rem)] items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold tracking-wide"
      style={{ backgroundColor: itemCard.pillSoftBg, color: itemCard.pillSoftFg }}
    >
      <span
        className="inline-block size-[7px] shrink-0 rounded-full"
        style={{ backgroundColor: accentColor ?? itemCard.stateAccent.offers }}
      />
      <span className="min-w-0 truncate">{text}</span>
    </span>
  );
}

function GoneOverlay({ head, sub }: { head: string; sub: string }) {
  return (
    <>
      <div className="absolute inset-0 z-20" style={{ backgroundColor: itemCard.goneTint }} />
      <div className="absolute inset-0 z-20 flex items-center justify-center p-3.5">
        <div
          className="-rotate-[4deg] border-[1.5px] px-4 py-2.5 text-center"
          style={{ borderColor: itemCard.goneStampBorder }}
        >
          <p
            className="text-xl font-extrabold tracking-[0.25em]"
            style={{ color: itemCard.goneStampFg }}
          >
            {head}
          </p>
          <p
            className="mt-1 text-[8.5px] font-semibold tracking-[0.14em] opacity-90"
            style={{ color: itemCard.goneStampFg }}
          >
            {sub}
          </p>
        </div>
      </div>
    </>
  );
}

export function ItemCard({
  listingId,
  title,
  price,
  currency,
  image_urls,
  scheduledPublishAt = null,
  listingFormat = null,
  discoverTypeLabel = null,
  stock = Infinity,
  sellerBanned = false,
  isUnlisted = false,
  onClick,
  interactive = true,
  imageLoading = "lazy",
  className,
  currentUserId,
  sellerId = "",
  sellerName = "",
  sellerAvatarUrl,
  condition = "used",
  description = "",
  shippingMethods = [],
  cartAction = "unified",
  buyNow = true,
  listingType = null,
  offersPendingCount = null,
  auctionEndAt = null,
  auctionStatus = null,
  auctionBidCount = null,
  initialSaved = false,
  savesCount,
  showBookmark = true,
  showCta = true,
}: ItemCardProps) {
  const t = useTranslations("itemCardBrowse");
  const locale = useLocale();
  const router = useRouter();
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [auctionCountdown, setAuctionCountdown] = useState<string | null>(null);
  const [preorderCountdown, setPreorderCountdown] = useState<string | null>(null);
  const [notifyDone, setNotifyDone] = useState(false);
  const [watchers, setWatchers] = useState(savesCount ?? 0);

  const { saved, toggleSaved } = useSavedState({
    itemId: listingId,
    itemType: "listing",
    initialSaved,
    currentUserId,
  });

  useEffect(() => {
    if (savesCount !== undefined) setWatchers(savesCount);
  }, [savesCount]);

  useEffect(() => {
    if (!saved) return;
    setWatchers((c) => Math.max(c, 1));
  }, [saved]);

  useEffect(() => {
    // Parent-supplied count is authoritative (feed API computes it in SQL and
    // ships it in the payload — see @kraahl/contract saves_count). Self-fetch
    // only for legacy call sites that don't pass it (e.g. search results);
    // otherwise every card = 2 browser->Supabase round-trips (the feed N+1).
    if (savesCount !== undefined) return;
    let active = true;
    const loadCount = async () => {
      try {
        const supabase = createClient();
        const { count, error } = await supabase
          .from("saved")
          .select("*", { count: "exact", head: true })
          .eq("item_type", "listing")
          .eq("item_id", listingId)
          .is("deleted_at", null);
        if (error) throw error;
        if (active) setWatchers(count ?? 0);
      } catch {
        if (active) setWatchers(0);
      }
    };
    void loadCount();
    return () => {
      active = false;
    };
  }, [listingId, savesCount]);

  const images = useMemo(() => {
    if (Array.isArray(image_urls)) return image_urls;
    try {
      return JSON.parse(image_urls) as string[];
    } catch {
      return [];
    }
  }, [image_urls]);

  // Resolve a bucket-relative storage path before it reaches the image wrapper.
  // `resolveStorageUrl` is idempotent, so the second resolve inside
  // `useHeicImage` is a no-op; doing it here keeps `firstImage` a real URL for
  // anything else that reads it. @see lib/storage/url.ts
  //
  // This file used to hand-roll the whole HEIC funnel here — a `displayImageSrc`
  // state, an `isConverting` flag, a `blobUrlRef` and a revoke-on-unmount effect,
  // all a copy of `hooks/use-heic-image.ts`. `ListingImage` owns that now
  // (handoff §9 step 8: one wrapper, not two), which is also what let the
  // component stop caring whether the photo had loaded yet.
  const firstImage = resolveStorageUrl(images[0] ?? "/placeholder.svg");

  useEffect(() => {
    if (listingFormat !== "auction" || !auctionEndAt) {
      setAuctionCountdown(null);
      return;
    }
    const end = new Date(auctionEndAt).getTime();
    const labels = { d: "d", h: "h", m: "m", s: "s" };
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setAuctionCountdown(left > 0 ? formatAuctionCountdown(left, labels) : null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [listingFormat, auctionEndAt]);

  useEffect(() => {
    if (!scheduledPublishAt) {
      setPreorderCountdown(null);
      return;
    }
    const end = new Date(scheduledPublishAt).getTime();
    if (Number.isNaN(end) || end <= Date.now()) {
      setPreorderCountdown(null);
      return;
    }
    const labels = { d: "d", h: "h", m: "m", s: "s" };
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setPreorderCountdown(left > 0 ? formatAuctionCountdown(left, labels) : null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [scheduledPublishAt]);

  const isOwner = Boolean(currentUserId && sellerId && currentUserId === sellerId);
  const model = buildItemCardView({
    listing: {
      scheduled_publish_at: scheduledPublishAt,
      scheduledPublishAt,
      listing_format: listingFormat,
      listing_type: listingType === "preorder" ? "preorder" : listingType === "standard" ? "standard" : null,
      discoverTypeLabel,
      quantity: stock,
      auction_status: auctionStatus,
      offers_pending_count: offersPendingCount,
      auction_bid_count: auctionBidCount,
    },
    isOwner,
    auctionCountdown,
    preorderCountdown,
    notifyDone,
    showCta,
    t,
    locale,
  });

  const priceLabel = formatPrice(price, currency || "USD");
  const isGone = model.stateId === "sold";
  const listingUrl = `/listings/${listingId}`;
  const canLink = interactive && !isGone && !sellerBanned;
  const useUnified =
    interactive &&
    cartAction === "unified" &&
    !sellerBanned &&
    model.stateId !== "sold" &&
    model.stateId !== "own";

  const handleStaticCtaClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!interactive || model.ctaDisabled) return;

      if (model.stateId === "sold" && !isOwner) {
        router.push(`/search?q=${encodeURIComponent(title)}`);
        return;
      }

      if (model.stateId === "own" || (model.stateId === "sold" && isOwner)) {
        editListingEvents.open({
          listingId,
          listing: {
            id: listingId,
            title,
            price,
            currency,
            image_urls: images,
            description,
            condition,
            listing_format: listingFormat,
          },
          messageId: "",
          roomName: "",
        });
      }
    },
    [
      interactive,
      model.ctaDisabled,
      model.stateId,
      isOwner,
      router,
      title,
      listingId,
      price,
      currency,
      images,
      description,
      condition,
      listingFormat,
    ],
  );

  const buyNowModalListing: BookmarkModalListing = useMemo(
    () => ({
      id: listingId,
      title,
      price,
      currency: currency ?? null,
      location: "",
      image_urls: images,
      description,
      condition,
      grade: "",
      created_at: "",
      username: sellerName || undefined,
      avatar_url: sellerAvatarUrl ?? null,
      scheduledPublishAt: scheduledPublishAt ?? null,
    }),
    [listingId, title, price, currency, images, description, condition, sellerName, sellerAvatarUrl, scheduledPublishAt],
  );

  const showOverlayPill = !isGone && model.pillMode === "urgent" && !!model.pillText;
  const showSoftPill = !isGone && model.pillMode === "soft" && !!model.pillText;
  const L = itemCard.layout;

  const imageBlock = (
    <div
      className="relative aspect-4/5 w-full overflow-hidden"
      style={{ borderRadius: L.photoRadiusPx, backgroundColor: itemCard.photoBg.dark }}
    >
      <div className="absolute inset-0 z-0 bg-neutral-900">
        {/*
          `TILE` — the rung written for THIS box (`aspect-4/5`), shared with the
          mobile grid card so whichever client renders a listing first warms the
          other's Supabase edge.

          It replaced `next/image` + `sizes="…50vw, …33vw, 25vw"`, which is a
          legitimate mechanism (handoff §11) but the wrong one here on two
          counts. (1) Cost: Next's cache key is `projectId + w + q + url +
          Accept`, `deviceSizes` has 14 entries and `formats` has 2, so this one
          component minted a billed transformation per (breakpoint x DPR x
          format) combination — and every MISS/STALE is charged again. (2)
          Accuracy: `25vw` describes the VIEWPORT, not the column. Inside the
          1400 px `container` with `xl:px-20` and `gap-x-5` the real 4-up column
          is ~295 CSS px, so at a 1512 px viewport the browser was asking for
          ~756 px and getting the 828 bucket — a 40 % overshoot in bytes AND a
          cache entry no other screen shares.

          512 x 640 is a downscale for every column except that widest desktop
          one, where it is 15 % short — the same band `CARD` already accepts, and
          an order below what the overshoot cost. @see lib/images/presets.ts
        */}
        <ListingImage
          src={firstImage}
          alt={title}
          preset={TILE}
          // Carries the `imageLoading="eager"` that the above-the-fold discover
          // row asks for (it was `loading={imageLoading}` on the old <Image>).
          // The wrapper spells eager loading as `priority`, which also sets
          // `fetchPriority="high"` — new, and correct for a row that is by
          // definition the first thing painted.
          priority={imageLoading === "eager"}
          // The scheduled-drop blur is the WRAPPER's job now — it applies the
          // same `SCHEDULED_LISTING_IMAGE_BLUR_CLASS` token this file used to
          // apply by hand. One spelling, one place.
          isScheduled={model.blurImage}
          skeletonClassName="animate-none bg-muted dark:bg-white/5"
          className={cn(isGone && "opacity-55")}
        />
      </div>

      {showOverlayPill && model.pillText ? (
        <StatusPill text={model.pillText} mode="urgent" />
      ) : null}
      {showSoftPill && model.pillText ? (
        <StatusPill text={model.pillText} mode="soft" accentColor={model.pillAccentColor} />
      ) : null}

      {showBookmark ? (
        <button
          type="button"
          className="absolute right-2.5 top-2.5 z-10 flex flex-col items-center gap-1"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!currentUserId) {
              setShowBookmarkModal(true);
              return;
            }
            void toggleSaved();
            setWatchers((c) => Math.max(0, c + (saved ? -1 : 1)));
          }}
          aria-label={saved ? t("unsaveA11y") : t("saveA11y")}
        >
          <span
            className="flex size-[30px] items-center justify-center rounded-full"
            style={{ backgroundColor: itemCard.bookmarkScrim }}
          >
            <Bookmark
              className="size-[15px]"
              style={{ color: itemCard.bookmarkCountInk }}
              fill={saved ? itemCard.bookmarkCountInk : "none"}
            />
          </span>
          {watchers > 0 ? (
            <span
              className="flex h-[18px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none"
              style={{
                backgroundColor: saved ? itemCard.bookmarkSavedBg : itemCard.bookmarkScrim,
                color: saved ? itemCard.bookmarkSavedInk : itemCard.bookmarkCountInk,
              }}
            >
              {watchers}
            </span>
          ) : null}
        </button>
      ) : null}

      {isGone ? <GoneOverlay head={model.goneHead} sub={model.goneSub} /> : null}
    </div>
  );

  const staticCtaClassName = cn(
    "inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-full px-2 text-[10.5px] font-semibold whitespace-nowrap",
    isGone
      ? "border border-border bg-transparent text-foreground"
      : model.ctaDisabled
        ? "bg-muted text-muted-foreground"
        : "bg-foreground text-background",
  );

  const isStaticCtaClickable =
    interactive && !model.ctaDisabled && (model.stateId === "sold" || model.stateId === "own");

  const staticCta = model.ctaLabel ? (
    isStaticCtaClickable ? (
      <button
        type="button"
        className={cn(staticCtaClassName, "cursor-pointer transition-opacity hover:opacity-90")}
        onClick={handleStaticCtaClick}
      >
        {ctaIcon(model.ctaIcon)}
        <span>{model.ctaLabel}</span>
      </button>
    ) : (
      <span className={staticCtaClassName}>
        {ctaIcon(model.ctaIcon)}
        <span>{model.ctaLabel}</span>
      </span>
    )
  ) : (
    <span className="inline-block h-8 w-[72px] shrink-0" />
  );

  const unifiedCta = useUnified ? (
    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
      <UnifiedAddToCartButton
        itemCard
        listingId={listingId}
        price={price}
        currency={currency}
        stock={stock}
        currentUserId={currentUserId}
        name={title}
        images={images}
        sellerId={sellerId}
        sellerName={sellerName || "Seller"}
        sellerAvatarUrl={sellerAvatarUrl ?? undefined}
        condition={condition}
        description={description}
        shippingMethods={shippingMethods as never[]}
        context="feed"
        uiVariant="default"
        scheduledPublishAt={scheduledPublishAt}
        sellerBanned={sellerBanned}
        isUnlisted={isUnlisted}
        listingFormat={listingFormat}
        discoverTypeLabel={discoverTypeLabel ?? undefined}
        listingType={listingType === "preorder" ? "preorder" : listingType === "standard" ? "standard" : null}
        buyNow={buyNow}
        onLoginRequiredForBuyNow={buyNow ? () => setShowBuyNowModal(true) : undefined}
      />
    </div>
  ) : (
    staticCta
  );

  const listingLinkProps = canLink
    ? {
        href: listingUrl,
        onClick: (e: MouseEvent) => {
          if (onClick) {
            e.preventDefault();
            onClick();
          }
        },
      }
    : null;

  const textBlock = (
    <>
      <h3
        className="shrink-0 font-semibold text-foreground"
        style={{
          fontSize: 15,
          lineHeight: `${L.titleLineHeight}px`,
          letterSpacing: "-0.02em",
          height: L.titleBlockHeight,
          minHeight: L.titleBlockHeight,
        }}
      >
        <span className="line-clamp-2">{title}</span>
      </h3>
    </>
  );

  const priceBlock = (
    <div
      className="min-w-0 flex-1"
      style={{ height: L.priceLineHeight + L.priceSubLineHeight }}
    >
      <p
        className={cn("font-semibold text-foreground", isGone && "text-muted-foreground line-through")}
        style={{ fontSize: 16, lineHeight: `${L.priceLineHeight}px`, letterSpacing: "-0.01em" }}
      >
        {priceLabel}
      </p>
      <p
        className={cn("text-muted-foreground", !model.priceSub && "opacity-0")}
        style={{ fontSize: 10.5, lineHeight: `${L.priceSubLineHeight}px` }}
      >
        {model.priceSub ?? "\u00A0"}
      </p>
    </div>
  );

  const body = (
    <div
      className="flex flex-col px-0.5"
      style={{ gap: L.titleToPriceGap, minHeight: L.bodyMinHeight }}
    >
      {listingLinkProps ? (
        <Link {...listingLinkProps} className="block min-w-0 shrink-0 no-underline">
          {textBlock}
        </Link>
      ) : (
        textBlock
      )}

      <div
        className="flex shrink-0 items-center justify-between gap-2"
        style={{ height: L.bottomRowHeight }}
      >
        {listingLinkProps ? (
          <Link {...listingLinkProps} className="min-w-0 flex-1 no-underline">
            {priceBlock}
          </Link>
        ) : (
          priceBlock
        )}
        {unifiedCta}
      </div>
    </div>
  );

  return (
    <>
      <BookmarkLoginModal
        open={showBookmarkModal}
        onClose={() => setShowBookmarkModal(false)}
        listing={buyNowModalListing}
      />
      <BookmarkLoginModal
        open={showBuyNowModal}
        onClose={() => setShowBuyNowModal(false)}
        listing={buyNowModalListing}
        variant="buyNow"
      />
      <div className={cn("group @container flex min-w-0 flex-col", className)} style={{ gap: L.shellGap }}>
        {canLink ? (
          <Link
            href={listingUrl}
            className="block"
            onClick={(e) => {
              if (onClick) {
                e.preventDefault();
                onClick();
              }
            }}
          >
            {imageBlock}
          </Link>
        ) : (
          <div className="block">{imageBlock}</div>
        )}

        <div className="min-w-0">{body}</div>
      </div>
    </>
  );
}
